/**
 * Global sound manager to handle sound settings across scenes
 */
import { StorageManager, GameSettings } from './StorageManager';

export class SoundManager {
  private static instance: SoundManager;
  private _soundEffectsEnabled: boolean = true;
  private _musicEnabled: boolean = true;
  private _isThemePlaying: boolean = false;
  private _currentTheme: Phaser.Sound.BaseSound | null = null;
  private _musicPosition: number = 0; // Track music position for better resume
  private storageManager: StorageManager;

  // Private constructor for singleton
  private constructor() {
    this.storageManager = StorageManager.getInstance();
    this.loadSettings();
  }

  // Load settings from storage
  private loadSettings(): void {
    const settings = this.storageManager.loadSettings();
    this._soundEffectsEnabled = settings.soundEffectsEnabled;
    this._musicEnabled = settings.musicEnabled;
  }

  // Save current settings to storage
  private saveSettings(): void {
    const settings: GameSettings = {
      soundEffectsEnabled: this._soundEffectsEnabled,
      musicEnabled: this._musicEnabled
    };
    this.storageManager.saveSettings(settings);
  }

  // Get singleton instance
  public static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  // Get sound effects state
  public get soundEffectsEnabled(): boolean {
    return this._soundEffectsEnabled;
  }

  // Set sound effects state
  public set soundEffectsEnabled(value: boolean) {
    this._soundEffectsEnabled = value;
    this.saveSettings();
  }
  
  // Get music state
  public get musicEnabled(): boolean {
    return this._musicEnabled;
  }

  // Set music state
  public set musicEnabled(value: boolean) {
    this._musicEnabled = value;
    this.saveSettings();
  }
  
  // Legacy getter for backward compatibility
  public get soundEnabled(): boolean {
    return this._soundEffectsEnabled && this._musicEnabled;
  }
  
  // Get theme playing state
  public get isThemePlaying(): boolean {
    return this._isThemePlaying;
  }
  
  // Set theme playing state
  public setThemePlaying(value: boolean): void {
    this._isThemePlaying = value;
  }

  // Toggle sound effects state and return new state
  public toggleSoundEffects(): boolean {
    this._soundEffectsEnabled = !this._soundEffectsEnabled;
    this.saveSettings();
    return this._soundEffectsEnabled;
  }
  
  // Toggle music state and return new state
  public toggleMusic(): boolean {
    this._musicEnabled = !this._musicEnabled;
    
    // If there's a current theme...
    if (this._currentTheme) {
      if (!this._musicEnabled && this._currentTheme.isPlaying) {
        // Pause the music if it's playing and we're disabling music
        this._currentTheme.pause();
        // We don't change _isThemePlaying here - we still want to remember that music was playing
      } else if (this._musicEnabled) {
        // If we're enabling music and there's a theme available, play it
        // regardless of isThemePlaying (fixes reload issue)
        if (this._currentTheme.isPlaying) {
          // Already playing, nothing to do
        } else if (this._currentTheme.isPaused) {
          // Resume if paused
          this._currentTheme.resume();
        } else {
          // Start from beginning if not playing or paused
          this._currentTheme.play();
        }
        this._isThemePlaying = true;
      }
    }
    
    this.saveSettings();
    return this._musicEnabled;
  }

  // Legacy method for backward compatibility
  public toggleSound(): boolean {
    const newState = !this._soundEffectsEnabled;
    this._soundEffectsEnabled = newState;
    this._musicEnabled = newState;
    
    if (!newState && this._currentTheme && this._currentTheme.isPlaying) {
      this._currentTheme.pause();
      // Don't change _isThemePlaying here
    }
    else if (newState && this._currentTheme) {
      // If we're enabling sound and there's a theme available, play it
      if (this._currentTheme.isPlaying) {
        // Already playing, nothing to do
      } else if (this._currentTheme.isPaused) {
        // Resume if paused
        this._currentTheme.resume();
      } else {
        // Start from beginning if not playing or paused
        this._currentTheme.play();
      }
      this._isThemePlaying = true;
    }
    
    this.saveSettings();
    return newState;
  }

  // Play theme music
  public toggleThemeMusic(themeMusic: Phaser.Sound.BaseSound) {
    // If we have a different current theme already, pause it first
    if (this._currentTheme && this._currentTheme !== themeMusic && this._currentTheme.isPlaying) {
      this._currentTheme.pause();
    }
    
    // Set this as the current theme
    this._currentTheme = themeMusic;
    
    if (this._musicEnabled) {
      if (this._isThemePlaying && themeMusic.isPlaying) {
        // Pause the current theme if it's playing
        themeMusic.pause();
        this._isThemePlaying = false;
      } else {
        // Resume or start playing the theme
        if (themeMusic.isPaused) {
          themeMusic.resume();
        } else {
          themeMusic.play();
        }
        this._isThemePlaying = true;
      }
    } else {
      // Even if music is disabled, mark that we want this theme to play when enabled
      this._isThemePlaying = true;
    }
  }
  
  // Get the current theme music instance
  public getCurrentTheme(): Phaser.Sound.BaseSound | null {
    return this._currentTheme;
  }
  
  // Stop any playing theme
  public stopCurrentTheme(): void {
    if (this._currentTheme && this._currentTheme.isPlaying) {
      this._currentTheme.stop();
      this._isThemePlaying = false;
    }
  }
  
  // Restart menu music from the beginning
  public restartMenuMusic(): void {
    if (this._currentTheme) {
      // Stop first to reset the position to the beginning
      if (this._currentTheme.isPlaying) {
        this._currentTheme.stop();
      }
      
      // Start playing from the beginning if music is enabled
      if (this._musicEnabled) {
        this._currentTheme.play();
        this._isThemePlaying = true;
      } else {
        // Just mark as wanting to play when enabled
        this._isThemePlaying = true;
      }
    }
  }
  
  /**
   * Check if a scene is currently in a game context (active gameplay)
   * @param scene The Phaser.Scene to check
   * @returns Boolean indicating if we're in a game context
   */
  public isInGameContext(scene: Phaser.Scene): boolean {
    // Check if GameScene is active or paused
    return (scene.scene.get('GameScene').scene.isActive() || 
            scene.scene.get('GameScene').scene.isPaused());
  }
} 